// src/components/map/useMapCredits.js
//
// The credits of the sources a map draws now (lib/map/mapCredits.js
// drawnCredits), kept current for MapCreditsFooter.jsx, and a re-render on
// each move so the Improve link follows the camera.
//
//   sourcedata   a layer shown or hidden (MapLibre's visibility event), a
//                source's metadata
//   styledata    a source or layer added or removed
//   idle         a credit rewritten in place (followMapboxCredit, on the
//                same sourcedata, may run after this hook's handler)
//
// A REMOVED MAP IS DROPPED, NEVER CALLED (DB, 2026-11-02). Explore's compare
// map is removed by MapView's effect while the page can still hand it to the
// footer for a render or two. On the map's "remove" event the hook lets it go
// (no credits, no live map for the Improve link); its cleanup then makes no
// call into it (MapLibre has dropped its listeners); and a map MapLibre has
// already marked removed (_removed) is not subscribed at all. The footer gets
// the map back from here only while it is live.
import { useEffect, useState } from "react";
import { drawnCredits } from "../../lib/map/mapCredits";

const EVENTS = ["load", "styledata", "sourcedata", "idle", "terrain"];

/** @returns {{credits: {html: string, styleId: string|null}[], map: object|null}} the credits, and the map while live */
export function useMapCredits(map) {
  // Keyed by the map, so a map gone or replaced shows nothing of the last one's.
  const [read, setRead] = useState({ map: null, credits: [], live: false });
  const [, setMoved] = useState(0);
  useEffect(() => {
    if (!map || map._removed) return undefined;
    let live = true;
    let last = null;
    const update = () => {
      if (!live) return;
      const next = drawnCredits(map);
      const key = JSON.stringify(next);
      if (key !== last) { last = key; setRead({ map, credits: next, live: true }); }
    };
    const moved = () => { if (live) setMoved((n) => n + 1); };
    const removed = () => { live = false; setRead({ map, credits: [], live: false }); };
    for (const e of EVENTS) map.on(e, update);
    map.on("moveend", moved);
    map.on("remove", removed);
    update();
    return () => {
      if (!live || map._removed) return;
      live = false;
      for (const e of EVENTS) map.off(e, update);
      map.off("moveend", moved);
      map.off("remove", removed);
    };
  }, [map]);
  const current = map && !map._removed && read.map === map && read.live ? map : null;
  return { credits: current ? read.credits : [], map: current };
}
